/**
 * Creating a library placeholder from a manually-added blank box (a part with no `sourcePlaceholderId`).
 *
 * Builds a {@link PlaceholderWriteRequest} from the part's current settings plus the optional subcategory /
 * destination the user picks in the confirm dialog, calls the shared create mutation, and refreshes the
 * palette so the new placeholder shows up immediately. The caller binds the returned id back onto the part.
 */
import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { useCreateProductsPlaceholder } from '@/api/generated/products-placeholders/products-placeholders';
import {
  PlaceholderStatus,
  PlaceholderType,
  type PlaceholderWriteRequest,
} from '@/api/generated/schemas';
import type { PlaceholderSubcategoryRef } from '@/modules/products/venua-planner/components/PlaceholderSubcategorySelector';
import type { DestinationRef } from '@/modules/products/venua-planner/venuaPlannerApi';
import type { BoardPart } from '../model';

/** Query key of the palette list — kept in sync with {@link usePalettePlaceholders}. */
const PALETTE_QUERY_KEY = ['venua-planner', 'costScheme', 'palette'] as const;

export interface CreatePlaceholderSelection {
  subcategory: PlaceholderSubcategoryRef | null;
  destination: DestinationRef | null;
}

/** Map a board part (+ picked refs) onto the placeholder write contract. Mirrors `toPlaceholderRequestBody`. */
function boardPartToPlaceholderRequest(
  part: BoardPart,
  { subcategory, destination }: CreatePlaceholderSelection
): PlaceholderWriteRequest {
  const isAccommodation = part.type === PlaceholderType.Accommodation;
  const isTransport = part.type === PlaceholderType.Transport;
  const name = part.name.trim();
  const note = part.note?.trim() ?? '';

  return {
    type: part.type,
    costType: part.costType,
    name: name.length > 0 ? name : null,
    subcategoryId: subcategory?.id ?? null,
    destinationId: destination?.destinationId ?? null,
    showInProgram: part.showInProgram,
    code: null,
    note: note.length > 0 ? note : null,
    status: PlaceholderStatus.Active,
    facilitySubTypeId: isAccommodation ? part.facilitySubTypeId : null,
    accommodationCategoryCbId: isAccommodation ? part.accommodationCategoryCbId : null,
    unitPatterns: isAccommodation ? part.unitPatterns : [],
    equipmentIds: isAccommodation ? part.equipmentIds : [],
    transportKind: isTransport ? part.transportKind : null,
  } satisfies PlaceholderWriteRequest;
}

export function useCreatePlaceholderFromPart() {
  const queryClient = useQueryClient();
  const createMutation = useCreateProductsPlaceholder();

  const createFromPart = useCallback(
    async (part: BoardPart, selection: CreatePlaceholderSelection): Promise<string> => {
      const data = boardPartToPlaceholderRequest(part, selection);
      const newId = await createMutation.mutateAsync({ data });
      await queryClient.invalidateQueries({ queryKey: PALETTE_QUERY_KEY });
      return newId;
    },
    [createMutation, queryClient]
  );

  return { createFromPart, isPending: createMutation.isPending };
}
